import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, MessageCircle, Send, Search, FileText, Download, MapPin, AlertTriangle, User,
  Star, Mail, MailOpen, Zap, Plus, Pencil, Trash2, X, Smile, Paperclip, Briefcase, PawPrint,
  Bold, Italic, Strikethrough, Merge,
} from "lucide-react";
import { apiSuperAdmin, mensajeError } from "../../api/client";
import { Spinner } from "../../components/ui/Spinner";
import { RelojFechaHora } from "../../components/ui/RelojFechaHora";
import { useSuperAdminAuth } from "../../context/SuperAdminAuthContext";
import { archivoABase64 } from "../../lib/archivo";
import { redimensionarImagenABase64 } from "../../lib/imagen";

type TipoMensaje = "TEXTO" | "IMAGEN" | "AUDIO" | "VIDEO" | "DOCUMENTO" | "UBICACION" | "OTRO";
type Direccion = "ENTRANTE" | "SALIENTE";
type Filtro = "todos" | "no_leidos" | "leidos" | "favoritos";
type OrigenTipo = "NEGOCIO" | "CLIENTE" | "DESCONOCIDO";
type FiltroOrigen = "todos" | "negocio" | "cliente" | "desconocido";

interface Conversacion {
  id: string;
  telefono: string;
  nombrePerfil?: string | null;
  ultimoMensaje?: string | null;
  ultimoMensajeEn?: string | null;
  noLeidos: number;
  favorito: boolean;
  // El número de la plataforma es uno solo y compartido por todos los
  // negocios — esto distingue si quien escribió es personal de un negocio
  // (dueño, veterinario, recepcionista) o el cliente final de alguno de
  // ellos, calculado en el backend comparando el teléfono normalizado.
  origenTipo?: OrigenTipo;
  origenNombre?: string | null;
  origenNegocio?: string | null;
}

interface Mensaje {
  id: string;
  conversacionId: string;
  direccion: Direccion;
  tipo: TipoMensaje;
  texto?: string | null;
  mediaId?: string | null;
  mediaMimeType?: string | null;
  archivoNombre?: string | null;
  createdAt: string;
}

interface Plantilla {
  id: string;
  titulo: string;
  texto: string;
}

function horaCorta(fecha: string): string {
  return new Date(fecha).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

function fechaCorta(fecha: string): string {
  const d = new Date(fecha);
  const hoy = new Date();
  if (d.toDateString() === hoy.toDateString()) return horaCorta(fecha);
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

// Clave por día (en horario de México) para agrupar mensajes y decidir dónde
// insertar el separador — así un mensaje de las 11pm y otro de la 1am del
// día siguiente no quedan agrupados como "el mismo día" solo por estar
// cerca en el tiempo.
function claveDia(fecha: string): string {
  return new Date(fecha).toLocaleDateString("es-MX", { timeZone: "America/Mexico_City" });
}

// Etiqueta amigable para el separador de día dentro de una conversación:
// "Hoy", "Ayer", o la fecha completa para cualquier otro día.
function etiquetaDia(fecha: string): string {
  const d = new Date(fecha);
  const hoy = new Date();
  const ayer = new Date();
  ayer.setDate(hoy.getDate() - 1);
  if (claveDia(fecha) === claveDia(hoy.toISOString())) return "Hoy";
  if (claveDia(fecha) === claveDia(ayer.toISOString())) return "Ayer";
  return d.toLocaleDateString("es-MX", { timeZone: "America/Mexico_City", weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

const FILTROS: { valor: Filtro; etiqueta: string }[] = [
  { valor: "todos", etiqueta: "Todos" },
  { valor: "no_leidos", etiqueta: "No leídos" },
  { valor: "leidos", etiqueta: "Leídos" },
  { valor: "favoritos", etiqueta: "Favoritos" },
];

const ORIGENES: { valor: FiltroOrigen; etiqueta: string }[] = [
  { valor: "todos", etiqueta: "Todos" },
  { valor: "negocio", etiqueta: "Negocios" },
  { valor: "cliente", etiqueta: "Clientes" },
  { valor: "desconocido", etiqueta: "Otros" },
];

// Etiqueta y color cortos para el badge de origen, tanto en la lista como en
// el encabezado de la conversación abierta.
function badgeOrigen(c: Pick<Conversacion, "origenTipo" | "origenNombre" | "origenNegocio">) {
  if (c.origenTipo === "NEGOCIO") {
    return { texto: c.origenNegocio ? `Negocio · ${c.origenNegocio}` : "Negocio", clase: "bg-indigo-100 text-indigo-700" };
  }
  if (c.origenTipo === "CLIENTE") {
    return { texto: c.origenNegocio ? `Cliente · ${c.origenNegocio}` : "Cliente", clase: "bg-emerald-100 text-emerald-700" };
  }
  return null;
}

// Selección curada de emojis frecuentes en atención al cliente (saludos,
// confirmaciones, mascotas, agradecimientos) — sin depender de ninguna
// librería externa, para no agregar peso ni riesgo de instalación al build.
const EMOJIS = [
  "😀", "😊", "😉", "😍", "🥰", "😘", "😁", "😅", "🙏", "👍",
  "👏", "🙌", "✅", "❤️", "💚", "💙", "⭐", "🎉", "😢", "😮",
  "🤔", "😴", "🐶", "🐱", "🐾", "🦴", "💉", "🩺", "🏥", "📅",
  "⏰", "📍", "📞", "💬", "✔️", "❌", "⚠️", "🔔", "💛", "🥳",
];

export default function WhatsappInbox() {
  const { superAdmin, cargando: cargandoAuth } = useSuperAdminAuth();
  const [conversaciones, setConversaciones] = useState<Conversacion[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [filtroOrigen, setFiltroOrigen] = useState<FiltroOrigen>("todos");
  const [seleccionadaId, setSeleccionadaId] = useState<string | null>(null);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [plantillas, setPlantillas] = useState<Plantilla[]>([]);
  const [modalPlantillasAbierto, setModalPlantillasAbierto] = useState(false);
  const [fusionando, setFusionando] = useState(false);

  async function cargarLista() {
    try {
      const res = await apiSuperAdmin.get("/superadmin/whatsapp/conversaciones");
      setConversaciones(res.data.items);
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoLista(false);
    }
  }

  async function cargarPlantillas() {
    try {
      const res = await apiSuperAdmin.get("/superadmin/whatsapp/plantillas");
      setPlantillas(res.data.items);
    } catch (err) {
      console.error(err);
    }
  }

  async function fusionarDuplicados() {
    if (fusionando) return;
    setFusionando(true);
    try {
      const res = await apiSuperAdmin.post("/superadmin/whatsapp/fusionar-duplicados");
      const { conversacionesFusionadas, numerosCorregidos } = res.data;
      if (conversacionesFusionadas === 0 && numerosCorregidos === 0) {
        alert("No se encontraron chats duplicados. Todo está en orden.");
      } else {
        alert(
          `Listo. Se combinaron ${conversacionesFusionadas} chat(s) duplicado(s) y se corrigió el número en ${numerosCorregidos} conversación(es).`
        );
      }
      cargarLista();
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setFusionando(false);
    }
  }

  useEffect(() => {
    cargarLista();
    cargarPlantillas();
    const id = setInterval(cargarLista, 8000);
    return () => clearInterval(id);
  }, []);

  async function alternarFavorito(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    setConversaciones((prev) => prev.map((c) => (c.id === id ? { ...c, favorito: !c.favorito } : c)));
    try {
      await apiSuperAdmin.patch(`/superadmin/whatsapp/conversaciones/${id}/favorito`);
    } catch (err) {
      console.error(err);
      cargarLista();
    }
  }

  async function alternarLeido(c: Conversacion, e: React.MouseEvent) {
    e.stopPropagation();
    const marcarComoLeido = c.noLeidos > 0;
    setConversaciones((prev) =>
      prev.map((x) => (x.id === c.id ? { ...x, noLeidos: marcarComoLeido ? 0 : Math.max(x.noLeidos, 1) } : x))
    );
    try {
      await apiSuperAdmin.patch(`/superadmin/whatsapp/conversaciones/${c.id}/leido`, { leido: marcarComoLeido });
    } catch (err) {
      console.error(err);
      cargarLista();
    }
  }

  if (!cargandoAuth && !superAdmin) return <Navigate to="/superadmin/login" replace />;

  const filtradas = conversaciones.filter((c) => {
    const q = busqueda.trim().toLowerCase();
    // El teléfono se guarda sin espacios ni símbolos (solo dígitos), pero la
    // gente normalmente escribe o pega el número con espacios, guiones,
    // paréntesis o el "+52" — antes se comparaba tal cual y esos símbolos
    // hacían que nunca coincidiera con nada. Aquí se le quita todo lo que no
    // sea dígito a lo que se escribió antes de comparar contra el teléfono.
    const qSoloDigitos = busqueda.replace(/\D/g, "");
    const coincideBusqueda =
      !q ||
      (c.nombrePerfil ?? "").toLowerCase().includes(q) ||
      (qSoloDigitos.length > 0 && c.telefono.includes(qSoloDigitos));
    if (!coincideBusqueda) return false;
    if (filtro === "no_leidos" && c.noLeidos === 0) return false;
    if (filtro === "leidos" && c.noLeidos !== 0) return false;
    if (filtro === "favoritos" && !c.favorito) return false;
    if (filtroOrigen === "negocio" && c.origenTipo !== "NEGOCIO") return false;
    if (filtroOrigen === "cliente" && c.origenTipo !== "CLIENTE") return false;
    if (filtroOrigen === "desconocido" && c.origenTipo && c.origenTipo !== "DESCONOCIDO") return false;
    return true;
  });

  return (
    <div className="fondo-marca-agua flex h-screen flex-col">
      <header className="bg-gradient-to-r from-slate-900 via-slate-900 to-blue-950 px-6 py-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/superadmin" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
              <ArrowLeft size={15} /> Volver al panel
            </Link>
            <h1 className="mt-2 flex items-center gap-2 text-xl font-bold">
              <MessageCircle size={20} className="text-emerald-400" /> Bandeja de WhatsApp
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <RelojFechaHora claro />
            <button
              onClick={fusionarDuplicados}
              disabled={fusionando}
              title="Combina chats duplicados del mismo número (por ejemplo, números de México con y sin el dígito extra)"
              className="flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-sm font-medium hover:bg-white/20 disabled:opacity-60"
            >
              {fusionando ? <Spinner className="h-[15px] w-[15px]" /> : <Merge size={15} className="text-sky-300" />}
              {fusionando ? "Combinando..." : "Combinar duplicados"}
            </button>
            <button
              onClick={() => setModalPlantillasAbierto(true)}
              className="flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-sm font-medium hover:bg-white/20"
            >
              <Zap size={15} className="text-amber-300" /> Respuestas rápidas
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Lista de conversaciones */}
        <aside className="flex w-80 shrink-0 flex-col border-r border-slate-200 bg-white">
          <div className="border-b border-slate-100 p-3">
            <div className="relative mb-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="input pl-8"
                placeholder="Buscar por nombre o número"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            <div className="mb-1.5 flex flex-wrap gap-1.5">
              {FILTROS.map((f) => (
                <button
                  key={f.valor}
                  onClick={() => setFiltro(f.valor)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    filtro === f.valor ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {f.etiqueta}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ORIGENES.map((o) => (
                <button
                  key={o.valor}
                  onClick={() => setFiltroOrigen(o.valor)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    filtroOrigen === o.valor ? "bg-slate-700 text-white" : "bg-slate-50 text-slate-400 hover:bg-slate-100"
                  }`}
                >
                  {o.etiqueta}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {cargandoLista ? (
              <div className="flex justify-center py-10"><Spinner /></div>
            ) : filtradas.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-400">
                {conversaciones.length === 0 ? "Todavía no ha llegado ningún mensaje." : "Sin resultados con este filtro."}
              </p>
            ) : (
              filtradas.map((c) => {
                const badge = badgeOrigen(c);
                return (
                <div
                  key={c.id}
                  onClick={() => setSeleccionadaId(c.id)}
                  className={`flex w-full cursor-pointer items-start gap-3 border-b border-slate-50 p-3 text-left hover:bg-slate-50 ${seleccionadaId === c.id ? "bg-blue-50" : ""}`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold uppercase text-slate-600">
                    {(c.nombrePerfil?.[0] ?? c.telefono[0]) || "?"}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-slate-700">{c.nombrePerfil || c.telefono}</span>
                      {c.ultimoMensajeEn && <span className="shrink-0 text-[11px] text-slate-400">{fechaCorta(c.ultimoMensajeEn)}</span>}
                    </span>
                    {badge && (
                      <span className={`mt-0.5 inline-block truncate rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${badge.clase}`}>
                        {badge.texto}
                      </span>
                    )}
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-slate-400">{c.ultimoMensaje || "—"}</span>
                      {c.noLeidos > 0 && (
                        <span className="flex h-4.5 min-w-[18px] shrink-0 items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold text-white">
                          {c.noLeidos}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-center gap-1">
                    <button
                      title={c.favorito ? "Quitar de favoritos" : "Marcar como favorito"}
                      onClick={(e) => alternarFavorito(c.id, e)}
                      className={`rounded-lg p-1 hover:bg-slate-100 ${c.favorito ? "text-amber-400" : "text-slate-300"}`}
                    >
                      <Star size={15} fill={c.favorito ? "currentColor" : "none"} />
                    </button>
                    <button
                      title={c.noLeidos > 0 ? "Marcar como leído" : "Marcar como no leído"}
                      onClick={(e) => alternarLeido(c, e)}
                      className="rounded-lg p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-500"
                    >
                      {c.noLeidos > 0 ? <Mail size={15} /> : <MailOpen size={15} />}
                    </button>
                  </span>
                </div>
                );
              })
            )}
          </div>
        </aside>

        {/* Hilo de mensajes */}
        <main className="flex flex-1 flex-col bg-slate-50">
          {seleccionadaId ? (
            <Conversacion
              conversacionId={seleccionadaId}
              conversacionInfo={conversaciones.find((c) => c.id === seleccionadaId)}
              onMensajeEnviado={cargarLista}
              plantillas={plantillas}
              onAbrirPlantillas={() => setModalPlantillasAbierto(true)}
            />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-slate-400">
              <MessageCircle size={36} />
              <p className="text-sm">Elige una conversación para ver los mensajes.</p>
            </div>
          )}
        </main>
      </div>

      {modalPlantillasAbierto && (
        <ModalPlantillas
          plantillas={plantillas}
          onCerrar={() => setModalPlantillasAbierto(false)}
          onCambio={cargarPlantillas}
        />
      )}
    </div>
  );
}

function Conversacion({
  conversacionId,
  conversacionInfo,
  onMensajeEnviado,
  plantillas,
  onAbrirPlantillas,
}: {
  conversacionId: string;
  conversacionInfo?: Conversacion;
  onMensajeEnviado: () => void;
  plantillas: Plantilla[];
  onAbrirPlantillas: () => void;
}) {
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [puedeResponder, setPuedeResponder] = useState(true);
  const [cargando, setCargando] = useState(true);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [mostrarPlantillas, setMostrarPlantillas] = useState(false);
  const [mostrarEmojis, setMostrarEmojis] = useState(false);
  const [enviandoArchivo, setEnviandoArchivo] = useState(false);
  const finRef = useRef<HTMLDivElement>(null);
  const archivoInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  async function cargar() {
    try {
      const res = await apiSuperAdmin.get(`/superadmin/whatsapp/conversaciones/${conversacionId}/mensajes`);
      setMensajes(res.data.mensajes);
      setPuedeResponder(res.data.puedeResponderLibremente);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    setCargando(true);
    setMostrarPlantillas(false);
    setMostrarEmojis(false);
    cargar();
    const id = setInterval(cargar, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversacionId]);

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensajes.length]);

  async function enviar() {
    if (!texto.trim()) return;
    setEnviando(true);
    setError("");
    try {
      await apiSuperAdmin.post(`/superadmin/whatsapp/conversaciones/${conversacionId}/responder`, { texto });
      setTexto("");
      await cargar();
      onMensajeEnviado();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  }

  // Envuelve la selección actual del textarea con el marcador de formato de
  // WhatsApp (*negritas*, _cursiva_, ~tachado~). Si no hay texto seleccionado,
  // inserta el par de marcadores vacío y deja el cursor entre ellos.
  function aplicarFormato(marcador: string) {
    const el = textareaRef.current;
    if (!el) return;
    const inicio = el.selectionStart ?? texto.length;
    const fin = el.selectionEnd ?? texto.length;
    const seleccion = texto.slice(inicio, fin);
    const nuevoTexto = texto.slice(0, inicio) + marcador + seleccion + marcador + texto.slice(fin);
    setTexto(nuevoTexto);
    requestAnimationFrame(() => {
      el.focus();
      const cursor = seleccion ? fin + marcador.length * 2 : inicio + marcador.length;
      el.setSelectionRange(cursor, cursor);
    });
  }

  const LIMITE_ADJUNTO_MB = 15;

  async function adjuntarArchivo(archivo: File) {
    if (archivo.size > LIMITE_ADJUNTO_MB * 1024 * 1024) {
      setError(`El archivo es demasiado grande (máximo ${LIMITE_ADJUNTO_MB} MB).`);
      return;
    }
    setEnviandoArchivo(true);
    setError("");
    try {
      let archivoBase64: string;
      let mimeType: string;
      if (archivo.type.startsWith("image/")) {
        // Se comprime antes de mandarla, igual que las demás fotos de la app.
        const dataUrl = await redimensionarImagenABase64(archivo, 1600, 0.82);
        archivoBase64 = dataUrl.split(",")[1] ?? dataUrl;
        mimeType = "image/jpeg";
      } else {
        archivoBase64 = await archivoABase64(archivo);
        mimeType = archivo.type || "application/octet-stream";
      }
      await apiSuperAdmin.post(`/superadmin/whatsapp/conversaciones/${conversacionId}/adjuntar`, {
        archivoBase64,
        mimeType,
        nombreArchivo: archivo.name,
        texto: texto.trim() || undefined,
      });
      setTexto("");
      await cargar();
      onMensajeEnviado();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviandoArchivo(false);
    }
  }

  return (
    <>
      <div className="border-b border-slate-200 bg-white px-5 py-3">
        <div className="flex items-center gap-2 font-semibold text-slate-700">
          <User size={16} className="text-slate-400" />
          {conversacionInfo?.nombrePerfil || conversacionInfo?.telefono}
          {conversacionInfo?.origenTipo === "NEGOCIO" && (
            <span className="flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
              <Briefcase size={11} /> {conversacionInfo.origenNegocio ? `Negocio · ${conversacionInfo.origenNegocio}` : "Negocio"}
            </span>
          )}
          {conversacionInfo?.origenTipo === "CLIENTE" && (
            <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
              <PawPrint size={11} /> {conversacionInfo.origenNegocio ? `Cliente · ${conversacionInfo.origenNegocio}` : "Cliente"}
            </span>
          )}
        </div>
        {conversacionInfo?.nombrePerfil && <div className="text-xs text-slate-400">{conversacionInfo.telefono}</div>}
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {cargando ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : (
          <div className="space-y-3">
            {mensajes.map((m, i) => {
              const esNuevoDia = i === 0 || claveDia(m.createdAt) !== claveDia(mensajes[i - 1].createdAt);
              return (
                <div key={m.id}>
                  {esNuevoDia && (
                    <div className="my-3 flex items-center justify-center">
                      <span className="rounded-full bg-slate-200/80 px-3 py-1 text-[11px] font-medium text-slate-500">
                        {etiquetaDia(m.createdAt)}
                      </span>
                    </div>
                  )}
                  <Burbuja mensaje={m} conversacionId={conversacionId} />
                </div>
              );
            })}
            <div ref={finRef} />
          </div>
        )}
      </div>

      <div className="relative border-t border-slate-200 bg-white p-4">
        {!puedeResponder && (
          <div className="mb-2 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-700">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            Ya pasaron más de 24 horas desde el último mensaje de este contacto — WhatsApp no permite mandarle texto libre hasta
            que él vuelva a escribir. Si necesitas contactarlo, usa una plantilla desde el módulo de Campañas.
          </div>
        )}
        {error && <div className="mb-2 text-xs text-red-500">{error}</div>}

        {mostrarPlantillas && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMostrarPlantillas(false)} />
            <div className="absolute bottom-full left-4 z-20 mb-2 max-h-72 w-80 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
              <div className="mb-1 flex items-center justify-between px-2 py-1">
                <span className="text-xs font-semibold text-slate-500">Respuestas rápidas</span>
                <button
                  onClick={() => {
                    setMostrarPlantillas(false);
                    onAbrirPlantillas();
                  }}
                  className="text-xs font-medium text-blue-600 hover:underline"
                >
                  Editar
                </button>
              </div>
              {plantillas.length === 0 ? (
                <p className="p-2 text-xs text-slate-400">
                  Todavía no tienes plantillas guardadas. Créalas con "Editar" para reutilizarlas aquí.
                </p>
              ) : (
                plantillas.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setTexto(p.texto);
                      setMostrarPlantillas(false);
                    }}
                    className="block w-full rounded-xl px-2 py-1.5 text-left hover:bg-slate-50"
                  >
                    <p className="text-xs font-semibold text-slate-700">{p.titulo}</p>
                    <p className="truncate text-xs text-slate-400">{p.texto}</p>
                  </button>
                ))
              )}
            </div>
          </>
        )}

        {mostrarEmojis && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMostrarEmojis(false)} />
            <div className="absolute bottom-full left-4 z-20 mb-2 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
              <div className="mb-1 flex items-center justify-between px-1 py-1">
                <span className="text-xs font-semibold text-slate-500">Emojis</span>
                <button onClick={() => setMostrarEmojis(false)} className="rounded-lg p-0.5 text-slate-400 hover:bg-slate-100">
                  <X size={14} />
                </button>
              </div>
              <div className="grid grid-cols-8 gap-0.5">
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => setTexto((t) => t + emoji)}
                    className="rounded-lg py-1 text-lg hover:bg-slate-100"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="mb-2 flex items-center gap-1">
          <button
            type="button"
            title="Negritas"
            onClick={() => aplicarFormato("*")}
            disabled={!puedeResponder || enviandoArchivo}
            className="flex shrink-0 items-center justify-center rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
          >
            <Bold size={14} />
          </button>
          <button
            type="button"
            title="Cursiva"
            onClick={() => aplicarFormato("_")}
            disabled={!puedeResponder || enviandoArchivo}
            className="flex shrink-0 items-center justify-center rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
          >
            <Italic size={14} />
          </button>
          <button
            type="button"
            title="Tachado (WhatsApp no permite subrayado, esto es lo más parecido)"
            onClick={() => aplicarFormato("~")}
            disabled={!puedeResponder || enviandoArchivo}
            className="flex shrink-0 items-center justify-center rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
          >
            <Strikethrough size={14} />
          </button>
          <span className="ml-1 text-[11px] text-slate-400">Enter = salto de línea · Ctrl+Enter = enviar</span>
        </div>
        <div className="flex gap-2">
          <button
            title="Emojis"
            onClick={() => {
              setMostrarPlantillas(false);
              setMostrarEmojis((v) => !v);
            }}
            disabled={!puedeResponder || enviandoArchivo}
            className="flex shrink-0 items-center justify-center rounded-xl border border-slate-200 px-3 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
          >
            <Smile size={16} />
          </button>
          <button
            title="Respuestas rápidas"
            onClick={() => {
              setMostrarEmojis(false);
              setMostrarPlantillas((v) => !v);
            }}
            disabled={!puedeResponder || enviandoArchivo}
            className="flex shrink-0 items-center justify-center rounded-xl border border-slate-200 px-3 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
          >
            <Zap size={16} />
          </button>
          <input
            ref={archivoInputRef}
            type="file"
            className="hidden"
            accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const archivo = e.target.files?.[0];
              e.target.value = "";
              if (archivo) adjuntarArchivo(archivo);
            }}
          />
          <button
            title="Adjuntar archivo o imagen"
            onClick={() => archivoInputRef.current?.click()}
            disabled={!puedeResponder || enviandoArchivo}
            className="flex shrink-0 items-center justify-center rounded-xl border border-slate-200 px-3 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
          >
            {enviandoArchivo ? <Spinner className="h-4 w-4" /> : <Paperclip size={16} />}
          </button>
          <textarea
            ref={textareaRef}
            className="input resize-none py-2 leading-snug"
            rows={Math.min(5, Math.max(1, texto.split("\n").length))}
            placeholder={puedeResponder ? "Escribe una respuesta... (Enter = salto de línea, Ctrl+Enter = enviar)" : "No disponible fuera de la ventana de 24 horas"}
            value={texto}
            disabled={!puedeResponder || enviando || enviandoArchivo}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                enviar();
              }
            }}
          />
          <button className="btn-primary self-end" onClick={enviar} disabled={!puedeResponder || enviando || enviandoArchivo || !texto.trim()}>
            {enviando ? <Spinner className="h-4 w-4" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </>
  );
}

function Burbuja({ mensaje, conversacionId }: { mensaje: Mensaje; conversacionId: string }) {
  const propio = mensaje.direccion === "SALIENTE";
  return (
    <div className={`flex ${propio ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[70%] rounded-2xl px-3.5 py-2 text-sm ${propio ? "bg-blue-500 text-white" : "bg-white text-slate-700 shadow-sm"}`}>
        <ContenidoMensaje mensaje={mensaje} conversacionId={conversacionId} propio={propio} />
        <div
          className={`mt-1 text-right text-[10px] ${propio ? "text-blue-100" : "text-slate-400"}`}
          title={new Date(mensaje.createdAt).toLocaleString("es-MX", { timeZone: "America/Mexico_City", dateStyle: "full", timeStyle: "short" })}
        >
          {horaCorta(mensaje.createdAt)}
        </div>
      </div>
    </div>
  );
}

function ContenidoMensaje({ mensaje, conversacionId, propio }: { mensaje: Mensaje; conversacionId: string; propio: boolean }) {
  const [urlMedia, setUrlMedia] = useState<string | null>(null);
  const [cargandoMedia, setCargandoMedia] = useState(false);

async function cargarMedia(): Promise<string | null> {
    if (urlMedia) return urlMedia;
    if (cargandoMedia || !mensaje.mediaId) return null;
    setCargandoMedia(true);
    try {
      const res = await apiSuperAdmin.get(`/superadmin/whatsapp/conversaciones/${conversacionId}/media/${mensaje.id}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setUrlMedia(url);
      return url;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setCargandoMedia(false);
    }
  }

  useEffect(() => {
    if (mensaje.tipo === "IMAGEN") cargarMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensaje.id]);

  if (mensaje.tipo === "TEXTO" || mensaje.tipo === "OTRO") {
    return <p className="whitespace-pre-wrap">{mensaje.texto || "(mensaje sin texto)"}</p>;
  }

  if (mensaje.tipo === "UBICACION") {
    return (
      <p className="flex items-center gap-1.5">
        <MapPin size={14} /> {mensaje.texto}
      </p>
    );
  }

  if (mensaje.tipo === "IMAGEN") {
    return (
      <div>
        {cargandoMedia ? (
          <div className="flex h-32 w-40 items-center justify-center"><Spinner className="h-4 w-4" /></div>
        ) : urlMedia ? (
          <img src={urlMedia} alt="" className="max-h-64 rounded-lg" />
        ) : (
          <span className="text-xs opacity-70">No se pudo cargar la imagen</span>
        )}
        {mensaje.texto && <p className="mt-1">{mensaje.texto}</p>}
      </div>
    );
  }

  // Audio, video, documento: botón de descarga bajo demanda (se pide el
  // archivo hasta que se hace clic, en vez de descargarlo siempre para
  // todos los mensajes que ni siquiera se lleguen a abrir).
  return (
    <button
      onClick={async () => {
        const url = await cargarMedia();
        if (url) window.open(url, "_blank");
      }}
      className={`flex items-center gap-2 text-sm ${propio ? "text-white" : "text-blue-600"}`}
    >
      {cargandoMedia ? <Spinner className="h-4 w-4" /> : <FileText size={16} />}
      {mensaje.texto || mensaje.archivoNombre || `Archivo (${mensaje.tipo.toLowerCase()})`}
      <Download size={14} />
    </button>
  );
}

function ModalPlantillas({
  plantillas,
  onCerrar,
  onCambio,
}: {
  plantillas: Plantilla[];
  onCerrar: () => void;
  onCambio: () => void;
}) {
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [titulo, setTitulo] = useState("");
  const [texto, setTexto] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [borrandoId, setBorrandoId] = useState<string | null>(null);
  const [error, setError] = useState("");

  function limpiarForm() {
    setEditandoId(null);
    setTitulo("");
    setTexto("");
    setError("");
  }

  function editar(p: Plantilla) {
    setEditandoId(p.id);
    setTitulo(p.titulo);
    setTexto(p.texto);
    setError("");
  }

  async function guardar() {
    if (!titulo.trim() || !texto.trim()) return;
    setGuardando(true);
    setError("");
    try {
      if (editandoId) {
        await apiSuperAdmin.put(`/superadmin/whatsapp/plantillas/${editandoId}`, { titulo, texto });
      } else {
        await apiSuperAdmin.post("/superadmin/whatsapp/plantillas", { titulo, texto });
      }
      limpiarForm();
      onCambio();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  async function borrar(id: string) {
    setBorrandoId(id);
    try {
      await apiSuperAdmin.delete(`/superadmin/whatsapp/plantillas/${id}`);
      if (editandoId === id) limpiarForm();
      onCambio();
    } catch (err) {
      console.error(err);
    } finally {
      setBorrandoId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="flex items-center gap-2 font-semibold text-slate-700">
            <Zap size={17} className="text-amber-400" /> Respuestas rápidas
          </h2>
          <button onClick={onCerrar} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {plantillas.length === 0 ? (
            <p className="text-sm text-slate-400">
              Todavía no tienes ninguna plantilla. Agrega la primera con el formulario de abajo — podrás usarla para
              contestar más rápido desde el ícono de rayo junto al mensaje.
            </p>
          ) : (
            <ul className="space-y-2">
              {plantillas.map((p) => (
                <li key={p.id} className="flex items-start justify-between gap-2 rounded-xl border border-slate-100 p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-700">{p.titulo}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-slate-400">{p.texto}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button onClick={() => editar(p)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600">
                      <Pencil size={15} />
                    </button>
                    <button
                      onClick={() => borrar(p.id)}
                      disabled={borrandoId === p.id}
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-600 disabled:opacity-50"
                    >
                      {borrandoId === p.id ? <Spinner className="h-3.5 w-3.5" /> : <Trash2 size={15} />}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-slate-100 bg-slate-50 p-5">
          <p className="label mb-2">{editandoId ? "Editar plantilla" : "Nueva plantilla"}</p>
          {error && <p className="mb-2 text-xs text-red-500">{error}</p>}
          <input
            className="input mb-2"
            placeholder="Título (ej. Horario de atención)"
            value={titulo}
            maxLength={80}
            onChange={(e) => setTitulo(e.target.value)}
          />
          <textarea
            className="input mb-3 min-h-[70px] resize-none"
            placeholder="Texto que se mandará al contacto..."
            value={texto}
            maxLength={1000}
            onChange={(e) => setTexto(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            {editandoId && (
              <button className="btn-secondary" onClick={limpiarForm}>
                Cancelar
              </button>
            )}
            <button className="btn-primary" onClick={guardar} disabled={guardando || !titulo.trim() || !texto.trim()}>
              {guardando ? <Spinner className="h-4 w-4" /> : editandoId ? <Pencil size={15} /> : <Plus size={15} />}
              {editandoId ? "Guardar cambios" : "Agregar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
